
// src/components/organizer/OrganizerStatsWidget.js
import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Box,
  Avatar,
  LinearProgress,
  Chip,
  Divider
} from '@mui/material';
import {
  Event as EventIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as TrophyIcon,
  Assignment as RegistrationIcon,
  CheckCircle as CompletedIcon,
  Schedule as UpcomingIcon
} from '@mui/icons-material';

const OrganizerStatsWidget = ({ data }) => {
  if (!data) return null;

  const statCards = [
    {
      title: 'Mis Eventos',
      value: data.totalEvents?.toString() || '0',
      icon: <EventIcon />,
      color: 'primary',
      trend: '+3',
      subtitle: `${data.activeEvents} activos`
    },
    {
      title: 'Participantes',
      value: data.totalParticipants?.toLocaleString() || '0',
      icon: <PeopleIcon />,
      color: 'success',
      trend: '+15%',
      subtitle: 'Total inscripciones'
    },
    {
      title: 'Ingresos',
      value: `$${(data.totalRevenue / 1000).toFixed(1)}K` || '$0',
      icon: <MoneyIcon />,
      color: 'info',
      trend: '+12%',
      subtitle: 'Generados'
    },
    {
      title: 'Tasa Éxito',
      value: `${data.successRate}%` || '0%',
      icon: <TrophyIcon />,
      color: 'warning',
      trend: '+5%',
      subtitle: 'Eventos completados'
    }
  ];

  const eventStats = [
    {
      status: 'Completados',
      count: data.completedEvents,
      percentage: (data.completedEvents / data.totalEvents * 100).toFixed(1),
      color: 'success',
      icon: <CompletedIcon />
    },
    {
      status: 'Activos',
      count: data.activeEvents,
      percentage: (data.activeEvents / data.totalEvents * 100).toFixed(1),
      color: 'primary',
      icon: <EventIcon />
    },
    {
      status: 'Próximos',
      count: data.upcomingEvents,
      percentage: (data.upcomingEvents / data.totalEvents * 100).toFixed(1),
      color: 'warning',
      icon: <UpcomingIcon />
    }
  ];

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title="📊 Mi Resumen"
        subheader="Estadísticas de mis eventos"
        action={
          <Chip 
            label="Organizer Pro" 
            color="primary" 
            size="small"
          />
        }
      />
      <CardContent>
        {/* Estadísticas principales */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {statCards.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Box
                sx={{
                  p: 1.5,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  textAlign: 'center',
                  height: '100%'
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: `${stat.color}.main`,
                    mx: 'auto',
                    mb: 1,
                    width: 32,
                    height: 32
                  }}
                >
                  {stat.icon}
                </Avatar>
                <Typography variant="h6" fontWeight="bold" color={`${stat.color}.main`}>
                  {stat.value}
                </Typography>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  {stat.title}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                  <Chip
                    label={stat.trend}
                    color={stat.trend.includes('+') ? 'success' : 'error'}
                    size="small"
                    sx={{ fontSize: '0.6rem', height: 16 }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {stat.subtitle}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Estado de eventos */}
        <Typography variant="h6" gutterBottom>
          🎯 Estado de Mis Eventos
        </Typography>
        
        <Grid container spacing={2}>
          {eventStats.map((stat, index) => (
            <Grid item xs={4} key={index}>
              <Box sx={{ p: 1.5, textAlign: 'center' }}>
                <Avatar 
                  sx={{ 
                    bgcolor: `${stat.color}.main`,
                    mx: 'auto',
                    mb: 1,
                    width: 28,
                    height: 28
                  }}
                >
                  {React.cloneElement(stat.icon, { fontSize: 'small' })}
                </Avatar>
                <Typography variant="h5" color={`${stat.color}.main`} fontWeight="bold">
                  {stat.count}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  {stat.status}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={parseFloat(stat.percentage)}
                  color={stat.color}
                  sx={{ height: 4, borderRadius: 2, mb: 0.5 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {stat.percentage}%
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Métricas adicionales */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="h6" color="success.main">
                  {((data.totalParticipants / data.totalEvents) || 0).toFixed(0)}
                </Typography>
                <Typography variant="caption">Promedio Participantes</Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary.main">
                  {data.averageRating || '4.8'}★
                </Typography>
                <Typography variant="caption">Calificación Promedio</Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="h6" color="info.main">
                  {data.repeatParticipants || '65'}%
                </Typography>
                <Typography variant="caption">Participantes Recurrentes</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default OrganizerStatsWidget;